import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Alert,
    Spinner,
    Button,
} from "react-bootstrap";

import "./epaper.css";

const API_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000";

const EPaper = () => {
    const [papers, setPapers] = useState([]);
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingPdf, setCheckingPdf] = useState(false);
    const [pdfError, setPdfError] = useState("");

    const pdfUrl = selectedPaper?.pdfUrl || "";

    /*
    |--------------------------------------------------------------------------
    | FETCH EPAPER DATA
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const loadEpaper = async () => {
            setLoading(true);

            try {
                // Latest Epaper
                const latestRes = await fetch(
                    `${API_URL}/api/epaper/latest`
                );

                const latestData =
                    await latestRes.json();

                if (latestData?.data) {
                    setSelectedPaper(
                        latestData.data
                    );
                }

                // Epaper List
                const listRes = await fetch(
                    `${API_URL}/api/epaper`
                );

                const listData =
                    await listRes.json();

                setPapers(listData?.data || []);

                // fallback
                if (!latestData?.data) {
                    if (
                        listData?.data?.length > 0
                    ) {
                        setSelectedPaper(
                            listData.data[0]
                        );
                    }
                }

            } catch (err) {
                console.error(
                    "API Error:",
                    err
                );

                setPdfError(
                    "Failed to load ePaper."
                );
            } finally {
                setLoading(false);
            }
        };

        loadEpaper();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | DISABLE SHORTCUTS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const handleKeyDown = (e) => {
            const key =
                e.key?.toLowerCase();

            if (
                (e.ctrlKey ||
                    e.metaKey) &&
                ["p", "s", "c", "u"].includes(
                    key
                )
            ) {
                e.preventDefault();
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () =>
            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
    }, []);

    return (
        <Container
            fluid
            className="epaper-wrapper"
        >
            {/* 📰 HEADER */}

            <div className="epaper-header">
                <h2>
                    🗞 ePaper Archive
                </h2>

                <div className="d-flex gap-2 align-items-center flex-wrap">

                    {/* 📅 DATE SELECT */}

                    <Form.Select
                        className="date-select"
                        value={
                            selectedPaper?.date ||
                            ""
                        }
                        onChange={(e) =>
                            setSelectedPaper(
                                papers.find(
                                    (p) =>
                                        p.date ===
                                        e.target
                                            .value
                                )
                            )
                        }
                        disabled={
                            loading ||
                            papers.length === 0
                        }
                    >
                        {loading &&
                        papers.length ===
                            0 ? (
                            <option>
                                Loading
                                ePaper
                                dates...
                            </option>
                        ) : papers.length >
                          0 ? (
                            papers.map((p) => (
                                <option
                                    key={
                                        p.date
                                    }
                                    value={
                                        p.date
                                    }
                                >
                                    {p.date}
                                </option>
                            ))
                        ) : selectedPaper ? (
                            <option
                                value={
                                    selectedPaper.date
                                }
                            >
                                {
                                    selectedPaper.date
                                }
                            </option>
                        ) : (
                            <option>
                                No ePaper
                                available
                            </option>
                        )}
                    </Form.Select>

                    {/* ⬇ DOWNLOAD BUTTON */}

                    {selectedPaper?.date && (
                        <Button
                            as="a"
                            href={`${API_URL}/api/epaper/download/${selectedPaper.date}`}
                            target="_blank"
                            rel="noreferrer"
                            variant="danger"
                        >
                            Download PDF
                        </Button>
                    )}
                </div>
            </div>

            {/* 🚫 NO PAPER */}

            {!selectedPaper ? (
                <Row className="py-5">
                    <Col className="text-center">
                        {loading ? (
                            <>
                                <Spinner animation="border" />

                                <div className="mt-3">
                                    Loading
                                    ePaper
                                    page...
                                </div>
                            </>
                        ) : (
                            <Alert variant="info">
                                No ePaper is
                                available
                                yet.
                                Please
                                check
                                back
                                later.
                            </Alert>
                        )}
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col
                        md={12}
                        className="viewer-panel"
                        onContextMenu={(
                            e
                        ) =>
                            e.preventDefault()
                        }
                    >
                        {/* 🔄 PDF LOADER */}

                        {checkingPdf && (
                            <div className="text-center py-5">
                                <Spinner animation="border" />

                                <div className="mt-3">
                                    Loading
                                    ePaper
                                    content...
                                </div>
                            </div>
                        )}

                        {/* ❌ PDF ERROR */}

                        {pdfError &&
                            !checkingPdf && (
                                <Alert variant="warning">
                                    {
                                        pdfError
                                    }

                                    <div className="mt-2">
                                        <a
                                            href={
                                                pdfUrl
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Open
                                            ePaper
                                            in a
                                            new
                                            tab
                                        </a>
                                    </div>
                                </Alert>
                            )}

                        {/* ✅ PDF VIEWER */}

                        {!checkingPdf &&
                            pdfUrl &&
                            !pdfError && (
                                <iframe
                                    title="ePaper Viewer"
                                    src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                    className="epaper-iframe"
                                />
                            )}

                        {/* 🚫 NO VIEWER */}

                        {!checkingPdf &&
                            !pdfUrl && (
                                <div className="text-center py-5">
                                    Viewer is
                                    not
                                    available
                                </div>
                            )}
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default EPaper;